import {Inject, Injectable, Optional} from "@angular/core";
import {BehaviorSubject, Subject} from "rxjs";
import {TIntColumnsData, TIntTableColumnsLimiterData} from "./types";
import {
  INT_TABLE_COLUMNS_LIMITER_DATA_EXCLUDE_ON_LOAD_FORMATTER,
  INT_TABLE_COLUMNS_LIMITER_DATA_EXCLUDE_ON_LOAD_LIST_TOKEN,
  INT_TABLE_COLUMNS_LIMITER_DATA_FORMATTER,
  INT_TABLE_COLUMNS_LIMITER_DATA_LIST_TOKEN,
  INT_TABLE_COLUMNS_LIMITER_DISPLAYED_PROP,
  INT_TABLE_COLUMNS_LIMITER_VALUE_PROP
} from "./tokens";
import {FormBuilder, FormGroup} from "@angular/forms";
import {takeUntil} from "rxjs/operators";
import {DestroyStreamService} from "../../services";

type AsyncReturnType<T extends (...args: any) => Promise<any>> =
  T extends (...args: any) => Promise<infer R> ? R : any

@Injectable()
export class IntTableColumnsLimiterService<T> {

  displayedList$: BehaviorSubject<TIntTableColumnsLimiterData<T>>
    = new BehaviorSubject<TIntTableColumnsLimiterData<T>>([]);
  formattedList$: BehaviorSubject<TIntTableColumnsLimiterData<T>>
    = new BehaviorSubject<TIntTableColumnsLimiterData<T>>([]);
  excludeOnLoadList$: BehaviorSubject<TIntTableColumnsLimiterData<T>>
    = new BehaviorSubject<TIntTableColumnsLimiterData<T>>([]);

  formHasBeenInit$ = new BehaviorSubject(false);

  form!: FormGroup;

  formHasBeenChange$ = new Subject();

  _completeList: TIntTableColumnsLimiterData<T> = [];

  constructor(
    @Inject(INT_TABLE_COLUMNS_LIMITER_DATA_LIST_TOKEN)
    private readonly _fullList: TIntTableColumnsLimiterData<T> | Promise<TIntTableColumnsLimiterData<T>>,
    @Optional()
    @Inject(INT_TABLE_COLUMNS_LIMITER_DATA_EXCLUDE_ON_LOAD_FORMATTER)
    private readonly excludeFormatter: (
      list: TIntTableColumnsLimiterData<T>,
      excludeList: TIntTableColumnsLimiterData<T>) => TIntTableColumnsLimiterData<T> | Promise<TIntTableColumnsLimiterData<T>>,
    @Optional()
    @Inject(INT_TABLE_COLUMNS_LIMITER_DATA_EXCLUDE_ON_LOAD_LIST_TOKEN)
    private readonly _excludeOnLoad: TIntTableColumnsLimiterData<T> | null,
    @Optional()
    @Inject(INT_TABLE_COLUMNS_LIMITER_DATA_FORMATTER)
    private readonly formatter: (list: TIntTableColumnsLimiterData<T>) => TIntTableColumnsLimiterData<T>,
    @Optional()
    @Inject(INT_TABLE_COLUMNS_LIMITER_DISPLAYED_PROP)
    readonly displayedProp: string,
    @Optional()
    @Inject(INT_TABLE_COLUMNS_LIMITER_VALUE_PROP)
   readonly valueProp: string,
    readonly fb: FormBuilder,
    private readonly destroy$: DestroyStreamService,
  ) {
    if(
      this._excludeOnLoad !== null && this.excludeFormatter
    ) {
      this.excludeOnLoadList$.next(this._excludeOnLoad);
      Promise.resolve(this._fullList)
        .then((list) => {
          return Promise
            .resolve(excludeFormatter(list, this._excludeOnLoad || []))
            .then((data) => {
              return {
                currentList: data,
                fullList: list
              }
            })
        })
        .then(({currentList, fullList}) => {
          this.setDisplayedList(currentList);
          this.setFormattedList(fullList);
          this._completeList = fullList;
          this.initForm();
        });

      return;
    }
    Promise
      .resolve(this._fullList)
      .then((list) => {
        this._completeList = list;
        this.setDisplayedList(list);
        this.setFormattedList(list);
        this.initForm();
      })

  }

  initForm() {
    this.form = this.fb.group(this.getAllCheckedFormModel());
    this.formHasBeenInit$.next(true);
    this.form.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(d => {
      const list = this.completeList.filter((item) => {
        const field = this.getValue(item);
        return d[field];
      });
      this.setDisplayedList(list);
    });

    this.formChangeListener();
  }

  private formChangeListener() {
    this.form.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((data) => {
      const map = Object.entries(data);
      this.formHasBeenChange$.next(map);
      map.forEach(([field, value]) => {
        this.form.controls[field].setValue(value, {onlySelf: true, emitEvent: false})
      })
    })
  }

  getAllCheckedFormModel() {
    const excludeOnLoad = this.excludeOnLoadList
      .map((i) => {
        return this.getValue(i);
      });
    const data =  this.completeList
      .map((item) => this.getValue(item))
      .reduce((acc, fieldName) => {
        const checked = excludeOnLoad !== null ? !excludeOnLoad.includes(fieldName) : true
        acc[fieldName] = [checked];
        return acc;
      }, {} as {[key: string]: [boolean]});
    return data;
  }

  showAll() {
    const patchData = this.completeList
      .map((item) => this.getValue(item))
      .reduce((acc, fieldName) => {
        acc[fieldName] = true;
        return acc;
      }, {} as {[key: string]: boolean});
    this.form.patchValue(patchData);
  }

  showOnly(columnsList: Array<T>) {
    const shownList = columnsList.map((c) => this.getValue(c));
    const patchData = this.completeList
      .map((item) => this.getValue(item))
      .reduce((acc, fieldName) => {
        acc[fieldName] = shownList.includes(fieldName);
        return acc;
      }, {} as {[key: string]: boolean});
    this.form.patchValue(patchData);
  }

  getLabel(record: T) {
    // @ts-ignore
    return this.displayedProp !== null ? `${record[this.displayedProp]}` : '';
  }

  getValue(record: T) {
    // @ts-ignore
    return this.valueProp !== null ? `${record[this.valueProp]}` : '';
  }

  setDisplayedList(list: TIntTableColumnsLimiterData<T>) {
    this.displayedList$.next(list);
  }

  defaultFormatter(list: TIntTableColumnsLimiterData<T>) {
    return list;
  }

  setFormattedList(list: TIntTableColumnsLimiterData<T>) {
    const formatter = this.formatter ?? this.defaultFormatter;
    this.formattedList$.next(formatter(list));
  }

  get isAllVisible() {
    return (this.completeList || []).length === (this.displayedList$.value || []).length;
  }

  get completeList() {
    return this._completeList;
  }

  get excludeOnLoadList() {
    return this.excludeOnLoadList$.value;
  }

  get list(): TIntColumnsData<T> {
    return this.displayedList$.value;
  }

  get formattedList() {
    return this.formattedList$.value;
  }
}
